import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import TokenService, { TokenRefreshError } from '~/services/token';
import { storeStateLoading } from '~/store/slices/loading';
// import mock from './useMock'

// mock(api)

const RETRIED_DUE_TO_401_KEY = '_retriedDueTo401';

const createApiInstance = (version: string = 'v1') => {
  const api = axios.create({
    baseURL: `${import.meta.env.VITE_ARIDE_API_URL}/${version}`
  });

  api.interceptors.request.use(
    (config) => {
      const authData = TokenService.getClientCredencialtoken();
      if (config && authData) {
        config.headers.Authorization = 'Bearer ' + authData;
      }
      storeStateLoading.startLoading();
      return config;
    },
    (error) => {
      storeStateLoading.stopLoading();
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      storeStateLoading.stopLoading();
      return response;
    },
    async (error) => {
      storeStateLoading.stopLoading();

      const originalRequest = error.config as InternalAxiosRequestConfig & { [RETRIED_DUE_TO_401_KEY]?: boolean };

      const is401 = error.response?.status === 401;
      const notYetRetried = originalRequest && !originalRequest[RETRIED_DUE_TO_401_KEY];

      if (is401 && notYetRetried) {
        originalRequest[RETRIED_DUE_TO_401_KEY] = true;
        try {
          await TokenService.refreshClientCredentialWithRetry();
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(
            refreshError instanceof TokenRefreshError
              ? refreshError
              : new TokenRefreshError('Não foi possível renovar o token. Tente fazer login novamente.', 3)
          );
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
};

export interface AuthenticatedEventSourceLike {
  onopen: (() => void) | null;
  onmessage: ((event: { data: string }) => void) | null;
  onerror: ((error: Event | Error) => void) | null;
  close: () => void;
}

export interface AuthenticatedEventSourceOptions {
  organization_id?: string | null;
}

const createAuthenticatedEventSource = (
  endpoint: string,
  options?: AuthenticatedEventSourceOptions
): AuthenticatedEventSourceLike => {
  const token = TokenService.getClientCredencialtoken();
  if (!token) {
    throw new Error('Token de autenticação não encontrado.');
  }

  const baseUrl = `${import.meta.env.VITE_ARIDE_API_URL}/v2`;
  const url = new URL(`${baseUrl}${endpoint}`);
  if (options?.organization_id) {
    url.searchParams.set('organization_id', options.organization_id);
  }
  const controller = new AbortController();

  const es: AuthenticatedEventSourceLike = {
    onopen: null,
    onmessage: null,
    onerror: null,
    close: () => controller.abort()
  };

  void Promise.resolve().then(async () => {
    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`
        },
        signal: controller.signal
      });

      if (!response.ok) {
        const err = new Error(`SSE request failed: ${response.status} ${response.statusText}`);
        es.onerror?.(err);
        return;
      }

      es.onopen?.();

      const reader = response.body?.getReader();
      if (!reader) {
        es.onerror?.(new Error('No response body'));
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();
            if (data && data !== '[DONE]') {
              try {
                es.onmessage?.({ data });
              } catch {}
            }
          }
        }
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      es.onerror?.(err instanceof Error ? err : new Error(String(err)));
    }
  });

  return es;
};

interface ApiInstance extends AxiosInstance {
  v2?: AxiosInstance;
}

const api = createApiInstance() as ApiInstance;
api.v2 = createApiInstance('v2');

export { createAuthenticatedEventSource };
export default api;
